import { uniqueId } from 'lodash';
import React, { useEffect, useState, useContext, useRef } from 'react';
import { BsPencilSquare } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { axiosPost, axiosUpdate, axiosGetAPI } from 'services';
import { authContext } from 'contexts/AuthContext';
import { DELIVERY_RATES_TABS_HEADING } from './constants';
import { DELIVERY_RATE_REQUEST_TABLE_COLUMNS } from './constant';

export const useRateCardManagement = () => {
  const [loading, setLoading] = useState(false);
  const defaultFilters = {
    pageNo: 0,
    pageSize: 999,
    fromPin: '',
    toPin: '',
  };
  const [filters, setFilters] = useState({ ...defaultFilters });
  const defaultValue = {
    id: '',
    entityName: 'Select From PIN',
  };
  const [selectedFromValue, setSelectedFromValue] = useState(defaultValue);
  const [selectedToValue, setSelectedToValue] = useState(defaultValue);
  const [fromOptions, setFromOptions] = useState(defaultValue);
  const [toOptions, setToOptions] = useState(defaultValue);
  const [searchString, setSearchString] = useState('');
  const { auth } = useContext(authContext);
  const abortControllerRef = useRef(null);
  const [matrixColumn, setMatrixColumn] = useState([]);
  const [matrixData, setMatrixData] = useState([]);
  const [rateLookUp, setRateLookUp] = useState([]);
  const [activeTab, setActiveTab] = useState(
    DELIVERY_RATES_TABS_HEADING.INDIAN,
  );
  const [isCreateRateOpen, setIsCreateRateOpen] = useState(false);
  const [isRateDetailsOpen, setIsRateDetails] = useState(false);
  const [cellSelectedFromPin, setCellSelectedFromPin] = useState(undefined);
  const [cellSelectedToPin, setCellSelectedToPin] = useState(undefined);
  const [editData, setEditData] = useState(undefined);
  const [refetchRateData, setRefetchRateData] = useState(false);
  const [advanceControlSetting, setAdvanceControlSetting] = useState({});

  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [advanceControlsOpen, setAdvanceControlsOpen] = useState(false);

  const [requestedDeliveryRateLoading, setRequestedDeliveryRateLoading] =
    useState(false);
  const [requestedDeliveryRate, setRequestedDeliveryRate] = useState([]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const fetchRates = async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setLoading(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const pageNo = typeof filters?.pageNo === 'number' ? filters.pageNo : 0;
      const pageSize =
        typeof filters?.pageSize === 'number' ? filters.pageSize : 9999;

      const params = new URLSearchParams();

      params.append('searchString', searchString ?? '');
      params.append('from', selectedFromValue?.id ?? '');
      params.append('to', selectedToValue?.id ?? '');
      params.append('pageSize', pageSize);
      params.append('pageNumber', pageNo);

      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate?${params.toString()}`,
        auth,
        { signal: controller.signal },
      );

      if (resp.isSuccessful) {
        setLoading(false);

        const rates = resp?.data.data;

        const uniqueFromColumns = Array.from(new Set(rates.map((r) => r.from)));
        const uniqueToColumns = Array.from(new Set(rates.map((r) => r.to)));
        const fromColumns = uniqueFromColumns.map((r) => {
          const fromColumn = rates.find((rate) => rate.from === r);
          return {
            id: fromColumn.id,
            name: fromColumn.from,
            cityName: fromColumn.fromCityName,
            stateName: fromColumn.fromStateName,
          };
        });
        const toColumns = uniqueToColumns.map((r) => {
          const toColumn = rates.find((rate) => rate.to === r);
          return {
            id: toColumn.id,
            name: toColumn.to,
            cityName: toColumn.toCityName,
            stateName: toColumn.toStateName,
          };
        });

        const rateLookup = {};
        rates.forEach((r) => {
          rateLookup[`${r.from}-${r.to}`] = r.rate;
        });
        setRateLookUp(rateLookUp);

        setMatrixColumn([
          {
            accessorKey: 'fromPin',
            header: 'From / To',
            enableSorting: false,
            cell: (info) => (
              <div>
                <span>
                  <b>{info.getValue().name}</b>
                </span>
                {info.getValue().cityName && (
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: 'none',
                    }}
                  >
                    {info.getValue().cityName}, {info.getValue().stateName}
                  </span>
                )}
              </div>
            ),
          },
          ...toColumns.map((pin) => ({
            accessorKey: pin.name,
            header: (
              <div>
                <span>{pin.name}</span>
                {pin.cityName && (
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: 'none',
                    }}
                  >
                    {pin.cityName}, {pin.stateName}
                  </span>
                )}
              </div>
            ),
            enableSorting: false,
            cell: (info) => info.getValue() || '-',
          })),
        ]);

        setMatrixData(
          fromColumns.map((fromPin) => {
            const row = { fromPin };
            toColumns.forEach((toPin) => {
              row[toPin.name] =
                rateLookup[`${fromPin.name}-${toPin.name}`] ?? '';
            });
            return row;
          }),
        );
      } else if (resp?.message?.code === 'ERR_CANCELED') {
        setLoading(true);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRequestedDeliveryRate = async () => {
    try {
      setRequestedDeliveryRateLoading(true);
      const response = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/requested-delivery-rates`,
        auth,
      );
      if (response.isSuccessful) {
        setRequestedDeliveryRate(
          response?.data?.data?.map((item, index) => {
            return {
              ...item,
              serialNumber: index + 1,
            };
          }),
        );
      } else {
        toast.error(response?.data?.message);
      }
    } catch (ex) {
    } finally {
      setRequestedDeliveryRateLoading(false);
    }
  };

  const fetchFromsTos = async () => {
    try {
      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/froms-tos`,
        auth,
      );

      if (resp.isSuccessful) {
        const fromsTos = resp?.data.data;

        setFromOptions([
          {
            id: '',
            entityName: 'All',
          },
          ...fromsTos.froms.map((fromColumn) => ({
            id: fromColumn,
            entityName: fromColumn,
          })),
        ]);
        setToOptions([
          {
            id: '',
            entityName: 'All',
          },
          ...fromsTos.tos.map((toColumn) => ({
            id: toColumn,
            entityName: toColumn,
          })),
        ]);
      } else {
        //
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAdvanceControlSettings = async () => {
    try {
      const response = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/get/app-settings?types=ADVANCE_CONTROLS`,
        auth,
      );
      if (response?.isSuccessful) {
        if (response?.data?.data?.length > 0) {
          setAdvanceControlSetting(response?.data?.data[0]);
        }
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  const handleCellClick = (cell) => {
    if (cell.column.id !== 'fromPin') {
      setCellSelectedFromPin(cell.row.original.fromPin.name);
      setCellSelectedToPin(cell.column.id);
      setIsRateDetails(true);
    }
  };

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();

      params.append('searchString', searchString ?? '');
      params.append('from', selectedFromValue?.id ?? '');
      params.append('to', selectedToValue?.id ?? '');

      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/download-excel?${params.toString()}`,
        auth,
        { responseType: 'blob' },
      );

      if (resp.isSuccessful) {
        const nowDate = new Date();
        const blobResponse = new Blob([resp.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blobResponse);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `delivery-rate-${nowDate.toDateString()}-${uniqueId()}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // console.error(resp);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeStatus = async (id, currentStatus) => {
    try {
      const params = new URLSearchParams();
      params.append('id', id);
      const data = {
        newStatus: currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
      };

      const resp = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/change-status/${id}`,
        data,
        auth?.data?.token,
      );

      if (resp.isSuccessful) {
        const newRate = resp.data.data;
        toast.success('Delivery rate status updated successfully');
        return newRate;
      }

      toast.error('Failed to update delivery rate status');
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleCreateRate = async (data) => {
    try {
      if (data.id) {
        const { id, ...newData } = data;
        const response = await axiosUpdate(
          `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/${data.id}`,
          newData,
          auth?.data.token,
        );

        if (response.isSuccessful) {
          toast.success('Delivery rate edited successfully');
        } else {
          toast.error('Failed to edit delivery rate');
        }
      } else {
        const response = await axiosPost(
          `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate`,
          data,
          {},
          auth,
        );

        if (response.isSuccessful) {
          toast.success('Delivery rate created successfully');
        } else {
          toast.error('Failed to create delivery rate');
        }
      }
      return true;
    } catch (ex) {
      console.error(ex);
      return false;
    } finally {
      setRefetchRateData((prev) => !prev);
      fetchRates();
      fetchRequestedDeliveryRate();
    }
  };

  const handleEditRate = (data) => {
    const date = new Date(data.validity * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const validity = `${year}-${month}-${day}`;

    setEditData({ ...data, validity });
    setIsCreateRateOpen(true);
  };

  const handleEditDeliveryRateRequest = (data) => {
    const dataToSetToEditState = {
      ...data,
      fromPin: data?.from,
      toPin: data?.to,
      deliveryRate: data?.rate,
      validity: data?.validityTill,
      status: data?.status,
      isRequestedOne: true,
    };

    setEditData(dataToSetToEditState);
    setIsCreateRateOpen(true);
  };

  const linkFormatter = (cell) => {
    return (
      <div
        className="text-center cursor-pointer"
        onClick={() => handleEditDeliveryRateRequest(cell?.row?.original)}
        aria-hidden
      >
        <BsPencilSquare size={16} />
      </div>
    );
  };

  const findIndexOfItem = (item) => {
    return DELIVERY_RATE_REQUEST_TABLE_COLUMNS.findIndex(
      (itm) => itm.accessorKey === item,
    );
  };
  DELIVERY_RATE_REQUEST_TABLE_COLUMNS[findIndexOfItem('action')].cell =
    linkFormatter;

  const handleCloseDetails = () => {
    setIsRateDetails(false);
    fetchRates();
    fetchFromsTos();
    fetchRequestedDeliveryRate();
  };

  const handleBulkUpload = async (file) => {
    try {
      setBulkUploadLoading(true);

      const formData = new FormData();
      formData.append('file', file);
      // Return the axiosPost promise here
      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/bulk-delivery-rate`,
        formData,
        {},
        auth,
      );
      if (response?.isSuccessful) {
        toast.success('Bulk delivery rates uploaded successfully!');
      } else {
        toast.error(`Failed with reason : ${response?.data?.message}`);
      }
      await fetchRates();
    } catch (ex) {
      console.error(ex);
    } finally {
      setBulkUploadLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    fetchFromsTos();
    fetchAdvanceControlSettings();
  }, []);

  useEffect(() => {
    setFilters({
      ...defaultFilters,
    });
    setMatrixData([]);
    fetchRates();
    fetchRequestedDeliveryRate();
  }, [searchString, selectedFromValue?.id, selectedToValue?.id]);

  return {
    loading,
    selectedFromValue,
    setSelectedFromValue,
    selectedToValue,
    setSelectedToValue,
    fromOptions,
    setFromOptions,
    toOptions,
    setToOptions,
    searchString,
    setSearchString,
    matrixColumn,
    matrixData,
    rateLookUp,
    activeTab,
    setActiveTab,
    isCreateRateOpen,
    setIsCreateRateOpen,
    isRateDetailsOpen,
    cellSelectedFromPin,
    cellSelectedToPin,
    editData,
    handleCellClick,
    handleChangeStatus,
    handleEditRate,
    handleCreateRate,
    downloadExcel,
    toggle,
    setEditData,
    defaultValue,
    setIsRateDetails,
    refetchRateData,
    handleCloseDetails,
    advanceControlSetting,
    bulkUploadLoading,
    bulkUploadOpen,
    setBulkUploadOpen,
    advanceControlsOpen,
    setAdvanceControlsOpen,
    handleBulkUpload,
    fetchAdvanceControlSettings,
    handleEditDeliveryRateRequest,
    requestedDeliveryRateLoading,
    requestedDeliveryRate,
  };
};
